import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ChevronLeft, ArrowUpRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProductCard } from "@/components/site/ProductCard";
import { CATEGORIES } from "@/data/products";
import { CATEGORY_CONTENT, CATEGORY_BANNERS } from "@/data/categoryContent";
import { Seo } from "@/components/seo/Seo";
import { breadcrumbLd, itemListLd } from "@/components/seo/jsonld";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_SIZE = 9;

export const Category = ({ cartCount, onAdd }) => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  const category = CATEGORIES.find((c) => c.slug === slug);
  const content = CATEGORY_CONTENT[slug];

  // Scroll to top when paginating
  useEffect(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
  }, [page]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setPage(1);
    axios.get(`${API}/products?category=${slug}`, { signal: controller.signal })
      .then((r) => {
        setProducts(r.data || []);
        setLoading(false);
        window.scrollTo({ top: 0, behavior: "instant" });
      })
      .catch((err) => {
        if (!axios.isCancel(err)) setLoading(false);
      });
    return () => controller.abort();
  }, [slug]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "latest") arr.sort((a, b) => (a.order || 0) - (b.order || 0));
    else if (sort === "price-asc") arr.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") arr.sort((a, b) => b.price - a.price);
    else if (sort === "name") arr.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "rating") arr.sort((a, b) => (b.review_summary?.avg || 0) - (a.review_summary?.avg || 0));
    return arr;
  }, [products, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageProducts = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const showingFrom = (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, sorted.length);

  if (!category) {
    return (
      <>
        <Header cartCount={cartCount} />
        <div className="max-w-[1400px] mx-auto px-6 pt-32 pb-32 text-center">
          <h1 className="font-heading text-4xl font-black">Category not found</h1>
          <Link to="/" className="mt-6 inline-block border-b border-black pb-1 text-sm font-medium">← Back home</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Seo
        title={`Wholesale ${category.name} — Buy Bulk at Discount Fillers`}
        description={`Buy wholesale ${category.name.toLowerCase()} at Discount Fillers. Authentic, lot-traceable, cold-chain shipping. Volume discounts from 5 units. ${category.tagline || ""}`.slice(0, 155)}
        keywords={`wholesale ${category.name.toLowerCase()}, buy ${category.name.toLowerCase()} online, ${slug} bulk pricing, ${slug} for clinics, cheap ${category.name.toLowerCase()}, ${category.name.toLowerCase()} for aesthetic professionals, Korean ${category.name.toLowerCase()}, MOQ 5 units, discount ${slug}, B2B ${slug}`}
        path={`/category/${slug}`}
        ogImage={CATEGORY_BANNERS[slug] || CATEGORY_BANNERS.botox}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", url: "/" },
            { name: category.name, url: `/category/${slug}` },
          ]),
          itemListLd(category.name, sorted),
        ]}
      />
      <Header cartCount={cartCount} />
      {/* Breadcrumb */}
      <nav className="max-w-[1400px] mx-auto px-6 pt-28 md:pt-32 pb-6 flex items-center gap-2 text-xs text-neutral-500 tracking-wide" data-testid="breadcrumb">
        <Link to="/" className="hover:text-black uppercase tracking-[0.2em]">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-black uppercase tracking-[0.2em] font-medium">{category.name}</span>
      </nav>

      {/* Banner */}
      <section className="max-w-[1400px] mx-auto px-6 pb-12" data-testid="category-banner">
        <div className="relative overflow-hidden rounded-3xl aspect-[21/8] md:aspect-[21/7] bg-neutral-100">
          <img
            src={CATEGORY_BANNERS[slug] || CATEGORY_BANNERS.botox}
            alt={`Wholesale ${category.name} — ${category.tagline || "B2B aesthetic supplies"}`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
          <div className="relative h-full flex flex-col justify-center px-6 md:px-14 lg:px-20 text-white max-w-3xl">
            <div className="text-[11px] tracking-[0.3em] uppercase font-medium opacity-90 mb-3">Wholesale category</div>
            <h1 className="font-heading text-4xl md:text-6xl tracking-tight leading-[0.95]">
              <span className="block font-black">{category.name}</span>
              <span className="block font-thin">{category.tagline}</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <section className="max-w-[1400px] mx-auto px-6 pb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="category-toolbar">
        <div className="text-sm text-neutral-600">
          Showing <span className="font-bold text-black">{showingFrom}–{showingTo}</span> of <span className="font-bold text-black">{sorted.length}</span> results
          <span className="ml-2 text-neutral-400 text-xs">Sorted by {sort.replace("-", " ")}</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <label className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">Sort:</label>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="border border-neutral-300 px-3 py-2 bg-white text-sm focus:outline-none focus:border-black"
            data-testid="sort-select"
          >
            <option value="latest">Latest</option>
            <option value="name">Name</option>
            <option value="price-asc">Price · low to high</option>
            <option value="price-desc">Price · high to low</option>
            <option value="rating">Rating</option>
          </select>
          <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium ml-2">Show:</span>
          <span className="border border-neutral-300 px-3 py-2 bg-white text-sm">{PAGE_SIZE} items</span>
        </div>
      </section>

      {/* Product grid (9-per-page perfect 3-col mosaic) */}
      <section className="max-w-[1400px] mx-auto px-6 pb-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-px bg-neutral-200" data-testid="category-loading">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-neutral-50 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 border-l border-t border-neutral-200" data-testid="category-grid">
            {pageProducts.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={onAdd} />
            ))}
          </div>
        )}
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <section className="max-w-[1400px] mx-auto px-6 pb-16 flex justify-center items-center gap-2" data-testid="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-2 border border-neutral-300 text-sm disabled:opacity-40 hover:bg-black hover:text-white hover:border-black transition-colors"
            data-testid="page-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              className={`px-4 py-2 text-sm border ${page === i + 1 ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
              data-testid={`page-${i + 1}`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-3 py-2 border border-neutral-300 text-sm disabled:opacity-40 hover:bg-black hover:text-white hover:border-black transition-colors"
            data-testid="page-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </section>
      )}

      {/* SEO content block */}
      {content && (
        <section className="bg-neutral-50 border-t border-neutral-200" data-testid="category-seo">
          <div className="max-w-[1100px] mx-auto px-6 py-20 md:py-24">
            <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tight leading-tight mb-8">
              {content.headline}
            </h2>
            <p className="text-base md:text-lg text-neutral-700 leading-relaxed font-light max-w-3xl">
              {content.intro}
            </p>
            <div className="mt-12 space-y-10">
              {content.sections.map((s, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-6 pb-10 border-b border-neutral-200 last:border-0">
                  <h3 className="md:col-span-4 font-heading text-xl font-black tracking-tight">{s.title}</h3>
                  <div className="md:col-span-8 space-y-4 text-base text-neutral-700 font-light leading-relaxed">
                    {s.body && <p>{s.body}</p>}
                    {s.bullets && (
                      <ul className="space-y-2">
                        {s.bullets.map((b, j) => (
                          <li key={j} className="flex gap-3"><span className="text-black">→</span><span>{b}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
};
